import queryString from 'query-string';
import type { APIError, Dispatch, GetState, ThunkAction, ThunkExtras } from '../../types/app.types';
import type { V2AddCartItems } from '../../types/api-v2.types';
import type { Item } from './cart-module';
import { addItems } from './cart-module';
import { BHP } from './delivery-module';
import { navigateToCheckoutStep } from './account-module';
import { getExperimentVariation } from './ab-module';
import type { Dosage, Drug, Drugs, DrugsState, Form, PriceTiers, Quantity } from './drugs.types';
import { getDosageMedId } from './drugs-module';
import { ENABLE_CONSULTATION_PRICE_CHANGE } from '../../config';

/* ------------------ constants ------------------ */
export const CONSULTATION_PRICE = ENABLE_CONSULTATION_PRICE_CHANGE ? 10 : 5;
export const CONSULTATION_RETRIEVAL_ATTEMPT_INTERVAL = 1500; // 1.5 seconds

/* ------------------ types ------------------ */
export type Consultation = {
  consultationId: string;
  status: {
    displayValue: string;
    value: string;
  };
  price: {
    displayValue: string;
    value: number;
  };
  createdOn: string;
  link: string;
  rawStatus: string;
  provider: string;
  updatedOn: string;
  creationStatus: string;
  isCompleted: boolean;
  condition: {
    displayValue: string;
    value: string;
  };
  orderId: string;
  chatLink?: string | null | undefined;
  startChat?: boolean | null | undefined;
  pharmacyInfo:
    | {
        instructions: string;
        name: string;
        addressLine1: string;
        addressLine2: string;
      }
    | null
    | undefined;
};
export type SelectedFunnelSource = {
  type?: string;
  telemedCondition?: string;
};
export type SelectedTreatmentType = {
  treatmentType?: string;
};
export type TelemedicineMarketingDrug = {
  displayPrice: string;
  slug: string;
  price: number;
  quantity: string;
};
export type Chat = Consultation; // Chat is the same type as Consultation

export type TelemedState = {
  consultations: Array<Consultation> | null | undefined;
  chats: Array<Chat> | null | undefined;
  // Used by Consultation Page to store the consultation retrieved
  // from the backend or the error retrieving it.
  consultation: Consultation | null | undefined;
  consultationError: string | null | undefined;
  // This keeps the count of the number of attempts to look if the
  // visit was created
  consultationRetrieveCount: number;
  conditionFunnelStep: any;
  selectedFunnelSource: SelectedFunnelSource;
  selectedTreatmentType: SelectedTreatmentType | null | undefined;
  telemedicines: Array<TelemedicineMarketingDrug> | null | undefined;
};
export type ValueProposition = {
  iconName: string;
  text: string;
};
export type howItWorksStep = {
  name: string;
  desc: string;
  mobileImgUrl: string;
  altTag: string;
};
export type faq = {
  key: string;
  question: string;
  answer: string;
};
export type faqs = {
  title: string;
  items: Array<faq>;
};
export type reason = {
  key: string;
  name: string;
};
export type footer = {
  title: string;
  text: string;
};
export type ConditionSlug = 'birth-control' | 'cholesterol';
export type Condition = {
  slug: string;
  name: string;
  title: string;
  mobileSubTextBold: string;
  mobileSubText: string;
  subTextBulletedList: Array<string>;
  isSubs: boolean;
  isRefillOnly: boolean;
  valuePropositions: Array<ValueProposition>;
  banner: string;
  commonDrugs: Array<string>;
  ctaSubtext: string;
  ctaText: string;
  ctaTitle: string;
  customerSex: string;
  subText: string;
  aboutTitle: string;
  aboutText: string;
  cta: string;
  marketingLogo: string;
  minimumMedicineCountToSearch: number;
  iamNotSureModalChanges: Array<string>;
  howItWorks: {
    title: string;
    steps: Array<howItWorksStep>;
  };
  reasonsToTry: {
    title: string;
    items: Array<reason>;
  };
  footer: footer;
  faqs: faqs;
};
export type ConditionMedDosage = {
  validQuantities: Array<number>;
};
export type ConditionMedDosages = Record<string, ConditionMedDosage>;
export type ConditionMed = {
  medNameId: string;
  slug: string;
  name: string;
  alternateName: string;
  highlight: boolean;
  validDosages: ConditionMedDosages;
};
// Condition Funnel's "Next" button action types:
type Redirect = {
  type: 'redirect';
  path: string;
};
type OpenModal = {
  type: 'open_modal';
  modal: string;
  modalText: any;
  modalExitPath: any;
};
export type ConditionFunnelAction = OpenModal | Redirect;
export type ConditionFunnelOption = {
  text: string;
  value: any;
  action: ConditionFunnelAction;
};
type ConditionFunnelStepType = 'multiple_choice' | 'dosage_selection' | 'med_substitution';
type ConditionFunnelStepName =
  | 'haveYouTakenThisMed'
  | 'doYouNeedNewRx'
  | 'isAcuteOrChronic'
  | 'isColdSoresOrGenitalHerpes'
  | 'selectMedDosage'
  | 'confirmMedSubstitution';
export type MultipleChoiceStepType = {
  id: string;
  path: string;
  name: ConditionFunnelStepName;
  type: ConditionFunnelStepType;
  title: string;
  text: string;
  options: Array<ConditionFunnelOption>;
};
export type DosageSelectionStepType = {
  id: string;
  path: string;
  name: ConditionFunnelStepName;
  type: ConditionFunnelStepType;
  title: string;
  text: string;
  action: ConditionFunnelAction;
};
export type MedSubstitutionStepType = MultipleChoiceStepType;
export type ConditionFunnelStep = MultipleChoiceStepType | DosageSelectionStepType | MedSubstitutionStepType;
export type selectedDrugDetails = {
  medNameId: string;
  form: Form;
  dosage: Dosage;
  quantity: Quantity;
  medId: string;
  price: PriceTiers;
};
export type conditionDetails = {
  marketingLogo: string;
  minimumMedicineCountToSearch: number;
  iamNotSureModalChanges: Array<string>;
  howItWorks: {
    title: string;
    steps: Array<howItWorksStep>;
  };
  reasonsToTry: {
    title: string;
    items: Array<reason>;
  };
  footer: footer;
  faqs: faqs;
};
type TelemedAddedProduct = {
  medId: string;
  quantity: number;
  priceId: string;
  isRecurringDefault?: boolean;
};
// Error actions
export type GetTelemedErrorAction = {
  type: 'telemed/PARTNER_ERROR';
} & APIError;
export type GetChatsErrorAction = {
  type: 'telemed/GET_CHATS_ERROR';
} & APIError;
export type GetConsultationsErrorAction = {
  type: 'telemed/CONSULTATIONS_ERROR';
} & APIError;
export type GetConsultationErrorAction = {
  type: 'telemed/CONSULTATION_ERROR';
  payload: string;
};
export type ChangePharmacyErrorAction = {
  type: 'telemed/CHANGE_PHARMACY_ERROR';
  payload: string;
};
// Consultation retrieval actions
export type GetChatsAction = {
  type: 'telemed/GET_CHATS';
  payload: Array<Chat>;
};
export type ConsultationAction = {
  type: 'telemed/GET_CONSULTATION';
  payload: {
    consultation: Consultation;
    consultationLifecycleStatus: string;
  };
};
export type GetFunnelStepAction = {
  type: 'telemed/GET_FUNNEL_STEP';
  payload: ConditionFunnelStep;
};
export type ClearFunnelStepAction = {
  type: 'telemed/CLEAR_FUNNEL_STEP';
};
export type SetFunnelSourceAction = {
  type: 'telemed/SET_FUNNEL_SOURCE';
  payload: SelectedFunnelSource;
};
export type ClearFunnelSourceAction = {
  type: 'telemed/CLEAR_FUNNEL_SOURCE';
};
export type SetTreatmentTypeAction = {
  type: 'telemed/SET_TREATMENT_TYPE';
  payload: SelectedTreatmentType;
};
export type ClearTreatmentTypeAction = {
  type: 'telemed/CLEAR_TREATMENT_TYPE';
};
export type ConsultationNotReadyAction = {
  type: 'telemed/CONSULTATION_NOT_READY';
};
export type ConsultationsAction = {
  type: 'telemed/GET_CONSULTATIONS';
  payload: Array<Consultation>;
};
export type SetTelemedicinesAction = {
  type: 'telemed/FETCH_TELEMEDICINES_SUCCESS';
  payload: Array<TelemedicineMarketingDrug>;
};
// Action bundle
export type TelemedAction =
  | GetTelemedErrorAction
  | GetChatsErrorAction
  | GetConsultationsErrorAction
  | GetConsultationErrorAction
  | ChangePharmacyErrorAction
  | GetChatsAction
  | ConsultationNotReadyAction
  | ConsultationsAction
  | ConsultationAction
  | GetFunnelStepAction
  | ClearFunnelStepAction
  | SetFunnelSourceAction
  | ClearFunnelSourceAction
  | SetTreatmentTypeAction
  | ClearTreatmentTypeAction
  | SetTelemedicinesAction;
export type TelehealthMedDetails = {
  isMedIdSubsEligible: boolean;
  isMedIdTelemedEligible: boolean;
  condition: string | null | undefined;
  eligibleQuantities: Array<number> | null | undefined;
};
// This has to be kept in sync with the backend definition and ideally
// with RX-WEB as well.
export class LifecycleStatus {
  static INCOMPLETE = 'incomplete';
  static AWAITING_DOCTOR_REVIEW = 'awaiting_doctor_review';
  static PRESCRIBED = 'prescribed';
  static NOT_PRESCRIBED = 'not_prescribed';
  static NOT_ELIGIBLE = 'not_eligible';
  static CANCELLED = 'cancelled';
  static CHAT_COMPLETED = 'completed';
}

export const isConsultationFinalized = (consultation: Consultation) => {
  return [LifecycleStatus.PRESCRIBED, LifecycleStatus.NOT_PRESCRIBED].includes(consultation.status.value);
};
export const isConsultationCompleted = (consultation: Consultation) => {
  return LifecycleStatus.INCOMPLETE !== consultation.status.value;
};

export function isBirthControl(drug: Drug) {
  return drug.telemedConditions.includes('birth-control');
}

/* ------------------ actions ------------------ */
function getConsultationsAction(consultations: Array<Consultation>): ConsultationsAction {
  return {
    type: 'telemed/GET_CONSULTATIONS',
    payload: consultations,
  };
}

function getChatsAction(chats: Array<Chat>): GetChatsAction {
  return {
    type: 'telemed/GET_CHATS',
    payload: chats,
  };
}

function getFunnelStepAction(step: ConditionFunnelStep): GetFunnelStepAction {
  return {
    type: 'telemed/GET_FUNNEL_STEP',
    payload: step,
  };
}

function clearFunnelStepAction(): ClearFunnelStepAction {
  return {
    type: 'telemed/CLEAR_FUNNEL_STEP',
  };
}

function setFunnelSourceAction(selectedFunnelSource: SelectedFunnelSource): SetFunnelSourceAction {
  return {
    type: 'telemed/SET_FUNNEL_SOURCE',
    payload: selectedFunnelSource,
  };
}

function clearFunnelSourceAction(): ClearFunnelSourceAction {
  return {
    type: 'telemed/CLEAR_FUNNEL_SOURCE',
  };
}

function setTreatmentTypeAction(selectedTreatmentType: SelectedTreatmentType): SetTreatmentTypeAction {
  return {
    type: 'telemed/SET_TREATMENT_TYPE',
    payload: selectedTreatmentType,
  };
}

function clearTreatmentTypeAction(): ClearTreatmentTypeAction {
  return {
    type: 'telemed/CLEAR_TREATMENT_TYPE',
  };
}

function setTelemedicinesAction(telemedicines: Array<TelemedicineMarketingDrug>): SetTelemedicinesAction {
  return {
    type: 'telemed/FETCH_TELEMEDICINES_SUCCESS',
    payload: telemedicines,
  };
}

/* ------------------ functions ------------------ */
const formatPath = (path: string) => {
  // Formats path string to start with '/' and not end with '/', e.g.: "/my/path"
  let formattedPath = path;

  if (path.endsWith('/')) {
    formattedPath = formattedPath.slice(0, -1);
  }

  if (!path.startsWith('/')) {
    formattedPath = `/${formattedPath}`;
  }

  return formattedPath;
};

export function navigateToConsultationsPage(): ThunkAction {
  return function (dispatch: Dispatch, getState: GetState, { history }: ThunkExtras): void {
    return history.push('/account/consultations');
  };
}

export const appendUrlPath = (url: string, appendPath: string) => {
  // Append path to url preserving query string
  const parsed = new URL(url);
  return `${parsed.origin}${parsed.pathname}${formatPath(appendPath)}${parsed.search}`;
};
export const replaceUrlPath = (url: string, newPath: string) => {
  // Replace path in url preserving query string
  const parsed = new URL(url);
  return `${parsed.origin}${formatPath(newPath)}${parsed.search}`;
};
export const navigateToPath = (push: (...args: Array<any>) => any, path: string) => {
  // Navigates to path preserving query string
  // Here :push is a react-router function that is connected to component props using withRouter()
  const parsed = new URL(window.location.href);
  push(`${formatPath(path)}${parsed.search}`);
  return Promise.resolve();
};

export function hasTelemedDrugs(cartItems: Array<Item>) {
  return cartItems.some((item) => item.isTelemedEligible);
}

export function getDrugSelections(drug: Drug) {
  const { selectedFormIndex: formIdx, selectedDosageIndex: dosageIdx, selectedQuantityIndex: qtyIdx } = drug;
  const medNameId = drug.medNameId;
  const form = drug.forms[formIdx];
  const dosage = drug.forms[formIdx].dosages[dosageIdx];
  const quantity = drug.forms[formIdx].dosages[dosageIdx].quantities[qtyIdx];
  return {
    medNameId,
    form,
    dosage,
    quantity,
    medId: dosage.medId,
    price: quantity.price,
  };
}

export function getSubstituteDrug(medId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const req = `/v2/telemed/substitute/${medId}`;
    return blinkAPIClient
      .get(req)
      .then((res) => res.data)
      .then((data) => data.result);
  };
}

export function clearFunnelStep(): ThunkAction<ClearFunnelStepAction> {
  return function (dispatch): ClearFunnelStepAction {
    return dispatch(clearFunnelStepAction());
  };
}

export function setFunnelSource(selectedFunnelSource: SelectedFunnelSource): ThunkAction<SetFunnelSourceAction> {
  return function (dispatch): SetFunnelSourceAction {
    return dispatch(setFunnelSourceAction(selectedFunnelSource));
  };
}

export function clearFunnelSource(): ThunkAction<ClearFunnelSourceAction> {
  return function (dispatch): ClearFunnelSourceAction {
    return dispatch(clearFunnelSourceAction());
  };
}

export function setTreatmentType(selectedTreatmentType: SelectedTreatmentType): ThunkAction<SetTreatmentTypeAction> {
  return function (dispatch): SetTreatmentTypeAction {
    return dispatch(setTreatmentTypeAction(selectedTreatmentType));
  };
}

export function clearTreatmentType(): ThunkAction<ClearTreatmentTypeAction> {
  return function (dispatch): ClearTreatmentTypeAction {
    return dispatch(clearTreatmentTypeAction());
  };
}

export const getTelemedCartCreationPayload = (drugs: Drugs, medSlug: string): TelemedAddedProduct => {
  // This has to be modified if same drug supports multiple conidtions
  // and default acute fdq is different based on the condition
  const acuteDefaultFDQ = [
    {
      med: 'valtrex',
      selectedFormIndex: 0,
      selectedDosageIndex: 0,
      selectedQuantityIndex: 1,
    },
    {
      med: 'valacyclovir',
      selectedFormIndex: 0,
      selectedDosageIndex: 0,
      selectedQuantityIndex: 1,
    },
  ];
  const drug = drugs[medSlug];
  const drugAcuteDefaultFDQ = acuteDefaultFDQ.filter((fdq) => fdq.med === medSlug.toLowerCase())[0];
  const {
    selectedFormIndex: find,
    selectedDosageIndex: dind,
    selectedQuantityIndex: qind,
  } = drugAcuteDefaultFDQ ? drugAcuteDefaultFDQ : drug;
  const selection = drug.forms[find].dosages[dind].quantities[qind];
  const medId = drug.forms[find].dosages[dind].medId;
  return {
    medId: medId,
    priceId: selection.price.delivery ? selection.price.delivery.priceId : '',
    quantity: selection.rawQuantity,
  };
};

export function createChatVisit(): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .post(`/v2/telemed/chat`)
      .then((res) => res.data)
      .then((data) => data.result)
      .then((result) => window.location.assign(result.chatUrl))
      .catch((err: APIError) =>
        dispatch({
          type: 'telemed/PARTNER_ERROR',
          ...err,
        })
      );
  };
}

export function getTelemedEligibility(
  medNameId: string,
  medId?: string,
  stateCode?: string
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    let req = `/v2/telemed/eligibility?med_name_id=${medNameId}`;
    if (medId) req += `&med_id=${medId}`;
    if (stateCode) req += `&state_code=${stateCode}`;
    return blinkAPIClient
      .get(req)
      .then((res) => res.data)
      .then((data) => data.result);
  };
}

export function changePharmacy(orderId: string, newPharmacyId: string | null | undefined): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    if (!newPharmacyId) {
      dispatch({
        type: 'telemed/CHANGE_PHARMACY_ERROR',
        payload: 'Provide non-empty newPharmacyId',
      });
    }

    return blinkAPIClient
      .patch(`/v2/telemed/order/${orderId}/pharmacy`, {
        pharmacy_id: newPharmacyId,
      })
      .catch((err: APIError) =>
        dispatch({
          type: 'telemed/CHANGE_PHARMACY_ERROR',
          payload: err.message,
        })
      );
  };
}

export function postTelemedEligibility(medNameId: string, medId: string, stateCode: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .post(`/v2/telemed/eligibility`, {
        medNameId,
        medId,
        stateCode,
      })
      .then((res) => res.data)
      .then((data) => data.result);
  };
}

export function forceAddDeliveryMedAndRedirectToCheckout(
  slug: string,
  medId: string,
  priceId: string,
  quantity: number
): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { history }: ThunkExtras): Promise<any> {
    const BHPlocationId = BHP.location && BHP.location.id;
    const payload: V2AddCartItems = {
      force: true,
      pharmacyId: BHPlocationId,
      items: [
        {
          medId,
          priceId,
          quantity,
        },
      ],
    };
    return dispatch(addItems(payload))
      .then(() => dispatch(navigateToCheckoutStep()))
      .catch(() => history.push(`/${slug}`));
  };
}

export function getFormulationEligibility(medNameId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const inExperiment = getExperimentVariation(getState().ab, 'dosage_on_rails') === 'variation';
    if (!inExperiment) return Promise.resolve([]);
    const req = `/v2/telemed/eligibility/cart/${medNameId}`;
    return blinkAPIClient
      .get(req)
      .then((res) => res.data)
      .then((data) => data.result || []);
  };
}

export function getOrCreateVisit(consultationId: string, count = 0): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .post('/v2/telemed/consultation', {
        consultationId,
      })
      .then((res) => res.data)
      .then((data) => {
        if (!data.result.creationStatus || data.result.creationStatus === 'creating') {
          // celery task isnt done yet, retry once. then the backend will syncronously retry
          if (count === 0) {
            return dispatch(getOrCreateVisit(consultationId, count + 1));
          }
        } else if (data.result.creationStatus === 'created') {
          // everything was ok, send forward
          return window.location.assign(data.result.link);
        }

        // this is the generic link without a visit, means everything failed and we dont
        // why this is happening
        return window.location.assign(data.result.link);
      })
      .catch((err: APIError) =>
        dispatch({
          type: 'telemed/PARTNER_ERROR',
          ...err,
        })
      );
  };
}

export function getConsultations(): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get('/v2/telemed/consultations')
      .then((res) => res.data)
      .then((data) => dispatch(getConsultationsAction(data.result)))
      .catch((err: APIError) => {
        dispatch({
          type: 'telemed/CONSULTATIONS_ERROR',
          ...err,
        });
      });
  };
}

export function getChats(): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`/v2/telemed/chats`)
      .then((res) => res.data)
      .then((data) => dispatch(getChatsAction(data.result)))
      .catch((err: APIError) =>
        dispatch({
          type: 'telemed/GET_CHATS_ERROR',
          ...err,
        })
      );
  };
}

/** Re-Auth user if they're coming from Firebolt
 *
 * This function will analyze `document.location.href' to know if user
 * has been kicked out from firebolt because of expired session.
 *
 * If that's the case, then it analyses `document.referrer' to try to grab
 * the visit ID from the referrer, then find the consultation related to
 * that visit and redirect user to that consultation.
 */
export function getUnauthorizedRedirect(document: any) {
  return async (dispatch: Dispatch, getState: GetState, { blinkAPIClient }: ThunkExtras): Promise<any> => {
    // Then we try to figure out where the user is coming from. If
    // there's a visit ID in the URL we try to send the user back where
    // they were.
    const match = /\/visits\/(\d+)/.exec(document.referrer);

    if (match) {
      // The bad news is that the UI doesn't know the consultation ID
      // of a visit coming from firebolt so we have to ask the backend
      // for that information.
      const visitId = match[1];
      const response = await blinkAPIClient.get(`/v2/telemed/consultation?visit_id=${visitId}`);
      const consultation = response.data.result;

      if (consultation.consultationId) {
        return `/consultation/${consultation.consultationId}`;
      }
    }

    return '/account/consultations';
  };
}

export function getLifecycleStatusFromHTTPReferrer(url: string) {
  const qs = queryString.parse(url.split('?').pop());
  const unescaped = unescape(unescape(qs.currentUrl as string)); // Firebolt does escape it twice

  // Protocol Referrals: The user can't continue their visit because
  // they might have conditions considered too serious for a telemed
  // visit. Since we'll trigger the refund, we make the call to tell
  // the user right away even though the webhook that makes it
  // official might not have arrived yet.
  // setting this to chat_completed because this takes us to the consultations page
  if (unescaped.endsWith('/follow_up_care')) return LifecycleStatus.CHAT_COMPLETED;
  // End of Consultation: The user just went through all the questions
  // in the interview. They're now waiting for the doctor's
  if (unescaped.endsWith('/confirmation')) return LifecycleStatus.AWAITING_DOCTOR_REVIEW;
  // Exit chat: The user opened a finished chat and pressed an Exit Chat button
  if (unescaped.endsWith('/chat')) return LifecycleStatus.CHAT_COMPLETED;
  // Didn't find anything interesting in the HTTP_REFERRER, let the
  // value of `consultation.status.value` be used instead.
  return null;
}

export type URLParameter = number | string | null | typeof undefined;

/** Retrieve a consultation with a visit from the backend
 *
 * This function retrieves the consultation referred to in the
 * parameter `consultationId` and check its `creationStatus` field. If
 * it's either `null' or `creating', it means the celery task that
 * creates the visit didn't finish and there's nothing to display to
 * the user. In that case, a new attempt is scheduled via `setTimeout'
 * until the visit is created.
 *
 * Whenever this function has a result, it will be pushed to the
 * global state under the `consultation' or `consultationError' keys.
 *
 * If you don't care about the creation status, don't use this
 * function. If you're sure the visit already exists, it's OK to use
 * this function though.
 */
export function getConsultation(consultationId: URLParameter, retrievalFailureCount = 0): ThunkAction<Promise<any>> {
  return async (dispatch: Dispatch, getState: GetState, { history, blinkAPIClient }: ThunkExtras): Promise<any> => {
    // Just make sure we're handling the right type of value
    // here. Ps.: We're attempting the integer conversion to make sure
    // we've got the right value type but we can't really use the
    // actual converted value because our backend IDs are bigger than
    // what javascript numbers support and when we convert them to
    // int, they overflow and look like different numbers.
    if (!consultationId || isNaN(parseInt(consultationId, 10)))
      return dispatch({
        type: 'telemed/CONSULTATION_ERROR',
        payload: 'Invalid Visit Id',
      });
    const url = `/v2/telemed/consultation?consultation_id=${consultationId}`;
    let consultation;

    // Retrieve consultation data from backend or fail gracefully.
    try {
      const response = await blinkAPIClient.get(url);
      consultation = response.data.result;
    } catch (err) {
      return dispatch({
        type: 'telemed/CONSULTATION_ERROR',
        payload: err.message,
      });
    }

    // Ensure visit was created in the provider. We have 4 states for
    // the creation of a visit:
    //  * null: creation not started (celery task didn't start yet)
    //  * creating: celery task has started but doesn't have a result yet
    //  * error: celery task wasn't able to create visit
    //  * created: visit was successfully created in Firebolt.
    // If the consultation is still not ready, we will make another attempt
    // to retrieve it after a set interval. We will continue to show a loading screen.
    // After several attempts, we will return an error.
    if ([null, '', 'creating'].includes(consultation.creationStatus)) {
      if (retrievalFailureCount >= 3) {
        return dispatch({
          type: 'telemed/CONSULTATION_ERROR',
          payload: "We're having trouble setting up your doctor visit.",
        });
      }

      // If the consultation is still not ready, we don't change
      // global status but we do register a new attempt to be made
      setTimeout(
        () => dispatch(getConsultation(consultationId, retrievalFailureCount + 1)),
        CONSULTATION_RETRIEVAL_ATTEMPT_INTERVAL
      );
      return dispatch({
        type: 'telemed/CONSULTATION_NOT_READY',
      });
    } else if (consultation.creationStatus === 'error') {
      // In the unlikely event of failure creating a consultation,
      // this function returns earlier and notifies the user
      // accordingly.
      return dispatch({
        type: 'telemed/CONSULTATION_ERROR',
        payload: 'There was an issue setting up your doctor visit.',
      });
    }

    // By this point we're sure the visit was properly created, now
    // we'll decide to either redirect the user to firebolt or to just
    // set the consultation to global state and let the router do the
    // rest.
    // HTTP Referrer has priority over database status because at this
    // point webhook didn't get through yet, so the UI knows more
    // about the visit state than the database.
    const consultationLifecycleStatus =
      getLifecycleStatusFromHTTPReferrer(document.referrer) || consultation.status.value;
    let consultationLink = consultation.link;

    // The visit exists and is associated to the consultation row.
    switch (consultationLifecycleStatus) {
      // Both statuses prescription-{added,denied} fall through
      // to the redirect case. They're mutating the consultation
      // link to include `/report` after the visit id.
      case LifecycleStatus.PRESCRIBED:
      // eslint-disable-next-line no-fallthrough
      case LifecycleStatus.NOT_PRESCRIBED: {
        const [base, params] = consultationLink.split('?');
        consultationLink = base + '/report?' + params;
      }
      // eslint-disable-next-line no-fallthrough
      case LifecycleStatus.NOT_ELIGIBLE:
      // eslint-disable-next-line no-fallthrough
      case LifecycleStatus.INCOMPLETE:
        // Now we're checking the status of the consultation in
        // the visit flow requires us to redirect the user to
        // Firebolt.
        window.location.assign(consultationLink);
        // Finishes sequence of fall through that started on
        // the PRESCRIBED case.
        return null;

      // Handle user kicked-out & end of chat visit
      case LifecycleStatus.CHAT_COMPLETED:
      // eslint-disable-next-line no-fallthrough
      case LifecycleStatus.CANCELLED:
        return history.push('/account/consultations');

      default:
        // The consultation isn't in any status that needs us to
        // redirect the user to Firebolt. We instead set the
        // consultation and its status in the global state and let
        // the layer above this thunk action (router or
        // decorators) make a decision about what to show.
        return dispatch({
          type: 'telemed/GET_CONSULTATION',
          payload: {
            consultation,
            consultationLifecycleStatus,
          },
        });
    }
  };
}

// cdp page get call
export function getCondition(conditionName: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`/v2/telemed/${conditionName}`)
      .then((res) => res.data)
      .then((data) => data.result)
      .catch((err) => err);
  };
}

// cdp medName get call
export function getConditionMedName(conditionName: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`/v2/telemed/${conditionName}/meds`)
      .then((res) => res.data)
      .then((data) => data.result);
  };
}

// cdp funnel-flow condition med get call
export function getConditionMed(conditionName: string, slug: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`/v2/telemed/${conditionName}/meds/${slug}`)
      .then((res) => res.data)
      .then((data) => data.result);
  };
}

// cdp funnel-flow step get call
export function getFunnelStep(
  conditionSlug: string,
  medSlug: string,
  stepIndex: string,
  medId: string,
  selectedFunnelSource: SelectedFunnelSource
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    let req = `/v2/telemed/${conditionSlug}/meds/${medSlug}/steps/${stepIndex}?med_id=${medId}`;
    if (selectedFunnelSource && selectedFunnelSource.type) req += `&source_type=${selectedFunnelSource.type}`;
    return blinkAPIClient
      .get(req)
      .then((res) => res.data)
      .then((data) => {
        const res = data.result;
        dispatch(getFunnelStepAction(res));
        return res;
      });
  };
}

export function fetchTelemedicines(): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`/v2/drugs/telemedicine_marketing`)
      .then((res) => res.data)
      .then((data) => dispatch(setTelemedicinesAction(data.result)));
  };
}

///////////////// Functions for limiting dosage/quantities in frontend /////////////////
export const filterToTelehealthQuantities = (
  allQtys: Array<Quantity>,
  restrictedQtys: Array<number>,
  currentQty: string
) => {
  //filter to limited quantities
  const validQuantities: Array<Quantity> = filterQtyList(allQtys, restrictedQtys);
  //get a new list and index for the <Select> element
  //if the original quantity isn't valid, also return a default idx in order to update Redux
  const newDisplayValues: Array<string> = validQuantities.map((q) => q.displayQuantity);
  let newDisplayIdx;
  let newSelectedQtyIdx;

  if (!newDisplayValues.includes(currentQty)) {
    newSelectedQtyIdx = getDefaultQtyIdx(allQtys, validQuantities);
    newDisplayIdx = 0;
  } else {
    newDisplayIdx = newDisplayValues.findIndex((q) => q === currentQty);
  }

  return [newDisplayValues, newDisplayIdx, newSelectedQtyIdx];
};
export const filterQtyList = (allQuantities: Array<Quantity>, validQuantities: Array<number>) => {
  return allQuantities.filter((qty) => {
    return validQuantities.includes(qty.rawQuantity);
  });
};
export const getDefaultQtyIdx = (allQuantities: Array<Quantity>, validQuantities: Array<Quantity>) => {
  const firstValidQty = validQuantities[0].rawQuantity;
  return allQuantities.findIndex((qty) => {
    return qty.rawQuantity === firstValidQty;
  });
};
export const convertSelectedIdxToActualIdx = (
  allQtys: Array<Quantity>,
  restrictedQtys: Array<number>,
  selectedQtyIdx: string
) => {
  //filter to limited quantities
  const validQuantities = filterQtyList(allQtys, restrictedQtys);
  const selectedQty = validQuantities.filter((qty, idx) => {
    return idx.toString() === selectedQtyIdx;
  })[0];
  return allQtys.findIndex((qty) => {
    return qty.displayQuantity === selectedQty.displayQuantity;
  });
};
export const medHasValidDosageQuantity = (med: selectedDrugDetails, conditionMed: ConditionMed) => {
  const medId = med.medId;
  const qty = med.quantity.rawQuantity;
  const validDosage = conditionMed.validDosages[medId];

  // for now we are only validating quantities, so if the medid is not found
  // inside med.validDosages, we just return true (no restrictions)
  if (!validDosage) {
    return true;
  } else {
    return validDosage.validQuantities.includes(qty);
  }
};
export const getValidQuantitiesForSelectedDrug = (drugs: DrugsState, conditionMed: ConditionMed) => {
  const selectedMedId = getDosageMedId(drugs);
  const validDosage = selectedMedId && conditionMed.validDosages[selectedMedId];
  return validDosage && validDosage.validQuantities;
};
export const getSubsFormsForDrug = (drug: Drug) => {
  return drug.forms.filter((form) => {
    return form.dosages.some((dosage) => dosage.telehealth.isMedIdSubsEligible);
  });
};
export const getSubsDosagesForForm = (form: Form) => {
  return form.dosages.filter((dosage) => {
    return dosage.telehealth.isMedIdSubsEligible;
  });
};
export const getSubsQuantitiesForDosage = (dosage: Dosage) => {
  const eligibleQuantities = dosage.quantities.filter((quantity) => {
    const quantities = dosage.telehealth.eligibleQuantities;
    return quantities && quantities.includes(quantity.rawQuantity);
  });
  return eligibleQuantities.length > 0 ? eligibleQuantities : dosage.quantities;
};
/* ------------------ reducer ------------------ */

export default function reducer(
  state: TelemedState = {
    consultation: null,
    consultationError: null,
    consultations: null,
    consultationRetrieveCount: 0,
    chats: null,
    conditionFunnelStep: null,
    selectedFunnelSource: {},
    selectedTreatmentType: null,
    telemedicines: [],
  },
  action: TelemedAction
) {
  switch (action.type) {
    case 'telemed/GET_CONSULTATION':
      return { ...state, ...action.payload };

    case 'telemed/CONSULTATION_NOT_READY':
      return { ...state, consultationRetrieveCount: (state.consultationRetrieveCount || 0) + 1 };

    case 'telemed/CONSULTATION_ERROR':
      return { ...state, consultationError: action.payload };

    case 'telemed/GET_CONSULTATIONS':
      return { ...state, consultations: action.payload };

    case 'telemed/GET_CHATS':
      return { ...state, chats: action.payload };

    case 'telemed/GET_FUNNEL_STEP':
      return { ...state, conditionFunnelStep: action.payload };

    case 'telemed/CLEAR_FUNNEL_STEP':
      return { ...state, conditionFunnelStep: null };

    case 'telemed/SET_FUNNEL_SOURCE':
      return { ...state, selectedFunnelSource: action.payload };

    case 'telemed/CLEAR_FUNNEL_SOURCE':
      return { ...state, selectedFunnelSource: null };

    case 'telemed/SET_TREATMENT_TYPE':
      return { ...state, selectedTreatmentType: action.payload };

    case 'telemed/CLEAR_TREATMENT_TYPE':
      return { ...state, selectedTreatmentType: null };

    case 'telemed/FETCH_TELEMEDICINES_SUCCESS':
      return { ...state, telemedicines: action.payload };

    default:
      return state;
  }
}
